/* ==========================================================================
   Filter bar — shared core of the collection and search pages
   Vanilla JS, no dependencies. Builds the filter bar (one pill per group,
   each with a floating panel; on mobile, a sheet that slides up with every
   group), asks Shopify for just the page's section (Section Rendering API,
   ?section_id=…) and swaps the product grid, the product count and the
   pagination, with pushState, back and forward. Also the sort select, the
   pagination without reloads and what the theme only sets up on page load
   (activate()).
   What depends on the page comes from an adapter, passed to
   FilterBar(adapter):
   - assets/collection-filters.js: tags in the collection path
     (/collections/<handle>/<tag>+<tag>).
   - assets/search-filters.js: tags in the search query (q=… AND tag:…).
   Loaded right before its adapter (both defer), from
   sections/collection-template.liquid and sections/search-template.liquid.

   Adapter (every function gets ctx first: { root, groups, data, state,
   avail, api }):
   - root, sectionId, prefix, sortId, dataId, metaId, historyKey: the
     section element, the section to ask for, the ids of the markup
     (<prefix>Pills, <prefix>Sheet, <prefix>SheetGroups; pills and panels
     <prefix>Pill-<name> / <prefix>Panel-<name>), the sort select, the JSON
     with the current view, the <template> with the tab title and the
     breadcrumb, and the key of our history entries.
   - groups(ctx): the filter groups. Options with "val" are buttons (one per
     group; "default": selected while the group has nothing, never stored),
     with "href" links ("Tipo de producto"); kind "custom" groups are built
     by the adapter (buildGroup, pillValue, syncGroup).
   - readState(ctx): the selection of the current URL, e.g. { kilj: '14k' }.
   - url(ctx, state, sortBy): the URL of a selection, on page 1.
   - availabilityKey / loadAvailability / isEmpty / loadOnInit / blocking:
     which options would give no results (see syncAvailable).
   - isCurrentLink(ctx, option), onInit(ctx), onRender(ctx, doc): optional.
   ctx.api: apply(inSheet), closePanel(), load(url, push, scrollUp) and
   loadAvailable() (the options' counts of the current view, without
   loading the results again; the search, once it stops looking further).
   ========================================================================== */
(function () {
  'use strict';

  if (window.FilterBar) return;

  var CLEAR = '[data-cf-clear]';
  var SHEET_OPEN = '[data-cf-sheet-open]';
  var SHEET_CLOSE = '[data-cf-sheet-close]';
  var SHEET_APPLY = '[data-cf-sheet-apply]';
  var BADGE = '[data-cf-count]';
  var SCROLL_LOCK = 'cf-sheet-open'; // on <html> while the sheet is open
  var GRID = '.shop-product-wrap';
  var COUNT = '.collection-hero__count';
  var PAGINATION = '[data-collection-pagination]';
  var LOADING = 'is-loading';
  var EMPTY = 'is-empty';

  var CHEVRON = '<svg class="cf-chevron" viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var CHECK = '<svg class="cf-check" viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M2.5 6.2 5 8.6l4.5-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function readJSON(scope, id) {
    var el = scope.querySelector('#' + id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function create(tag, className, text) {
    var el = document.createElement(tag);
    if (className) el.className = className;
    if (text) el.textContent = text;
    return el;
  }

  // Links in a Section Rendering response (pagination) can carry ?section_id=.
  // It never goes into a link or the address bar: reloading or sharing that
  // URL would open just the section's HTML.
  function withoutSectionId(url) {
    var clean = new URL(url, location.href);
    clean.searchParams.delete('section_id');
    return clean.pathname + clean.search + clean.hash;
  }

  function FilterBar(adapter) {
    var PILLS = '#' + adapter.prefix + 'Pills';
    var SHEET = '#' + adapter.prefix + 'Sheet';
    var SHEET_GROUPS = '#' + adapter.prefix + 'SheetGroups';
    var SORT = '#' + adapter.sortId;

    // root: section element (looked up again after a theme editor reload);
    // groups: filter groups; data: current view (url, count, sort…);
    // state: checked option per group, e.g. { kilj: '14k', publ: 'dama' };
    // avail: what the adapter loaded to know the options with no results
    var ctx = { root: null, groups: null, data: null, state: {}, avail: {} };
    var availFor = ''; // availabilityKey() that ctx.avail was loaded for
    var request;       // AbortController of the requests in flight
    var openGroup = null; // name of the group whose panel is open
    var sheetOpen = false;
    var sheetTimer;
    var mobile = window.matchMedia('(max-width: 767px)');

    ctx.api = {
      apply: apply,
      closePanel: closePanel,
      load: load,
      loadAvailable: loadAvailable
    };

    function groupNamed(name) {
      return ctx.groups.filter(function (group) { return group.name === name; })[0];
    }

    // Only "val" options: links have none
    function optionOf(group, value) {
      if (!group || value == null) return undefined;
      return group.options.filter(function (option) { return option.val === value; })[0];
    }

    // With nothing stored for the group, its "default" option is the selected one
    function isSelected(group, option) {
      var value = ctx.state[group.name];
      return value == null ? !!option.default : value === option.val;
    }

    // Pick an option; picking the selected one removes it (back to the
    // default, if the group has one). False if nothing changed.
    function setOption(group, option) {
      if (isSelected(group, option)) {
        if (option.default) return false;
        delete ctx.state[group.name];
      } else if (option.default) {
        delete ctx.state[group.name];
      } else {
        ctx.state[group.name] = option.val;
      }
      return true;
    }

    // After a change of ctx.state: the desktop panel closes; the mobile sheet
    // stays open so filters can be combined
    function apply(inSheet) {
      if (!inSheet) closePanel();
      syncSelected();
      load(adapter.url(ctx, ctx.state), true, false);
    }

    /* Filter bar ----------------------------------------------------------- */

    function optionButton(group, option, inSheet) {
      var button = create('button', 'cf-option');
      button.type = 'button';
      button.dataset.filter = group.name;
      button.dataset.value = option.val;
      button.innerHTML = CHECK;
      button.appendChild(create('span', '', option.label));
      button.addEventListener('click', function () {
        if (setOption(group, option)) apply(inSheet);
        else if (!inSheet) closePanel();
      });
      return button;
    }

    // "Tipo de producto": link to another collection, the current one marked
    function pageLink(option) {
      var link = create('a', 'cf-link');
      link.href = option.href;
      link.dataset.href = option.href;
      link.appendChild(create('span', '', option.label));
      if (adapter.isCurrentLink && adapter.isCurrentLink(ctx, option)) {
        link.classList.add('is-current');
        link.setAttribute('aria-current', 'page');
        link.insertAdjacentHTML('beforeend', CHECK);
      }
      return link;
    }

    function isLinks(group) {
      return group.options.some(function (option) { return option.href; });
    }

    function optionList(group, inSheet) {
      if (group.kind === 'custom') return adapter.buildGroup(ctx, group, inSheet);
      var list = create('div', isLinks(group) ? 'cf-links' : 'cf-options');
      group.options.forEach(function (option) {
        list.appendChild(option.href ? pageLink(option) : optionButton(group, option, inSheet));
      });
      return list;
    }

    function panelTitle(group) {
      if (group.panelTitle) return group.panelTitle;
      return isLinks(group) ? 'Ver otra categoría' : 'Elige una opción';
    }

    // Desktop: one pill per group, each with its floating panel.
    // Mobile: every group in the sheet, one below the other.
    function buildFilters() {
      var pills = ctx.root.querySelector(PILLS);
      var sheetGroups = ctx.root.querySelector(SHEET_GROUPS);

      if (pills) {
        pills.innerHTML = '';
        ctx.groups.forEach(function (group) {
          var links = group.kind !== 'custom' && isLinks(group);
          var box = create('div', 'cf-group');
          var pill = create('button', 'cf-pill');
          var panel = create('div', 'cf-panel' + (links ? ' cf-panel--links' : '') + (group.panelClass ? ' ' + group.panelClass : ''));

          box.dataset.group = group.name;

          pill.type = 'button';
          pill.id = adapter.prefix + 'Pill-' + group.name;
          pill.setAttribute('aria-expanded', 'false');
          pill.setAttribute('aria-controls', adapter.prefix + 'Panel-' + group.name);
          pill.innerHTML = '<span class="cf-pill__text"></span>' + CHEVRON;
          pill.addEventListener('click', function () {
            if (openGroup === group.name) closePanel();
            else openPanel(group.name);
          });

          panel.id = adapter.prefix + 'Panel-' + group.name;
          panel.hidden = true;
          panel.setAttribute('role', 'group');
          panel.setAttribute('aria-labelledby', pill.id);
          panel.appendChild(create('p', 'cf-panel__title', panelTitle(group)));
          panel.appendChild(optionList(group, false));

          box.appendChild(pill);
          box.appendChild(panel);
          pills.appendChild(box);
        });
      }

      if (sheetGroups) {
        sheetGroups.innerHTML = '';
        ctx.groups.forEach(function (group) {
          var box = create('div', 'cf-sheet__group');
          var list = optionList(group, true);
          list.setAttribute('role', 'group');
          list.setAttribute('aria-label', group.label);
          box.appendChild(create('p', 'cf-sheet__group-title', group.label));
          box.appendChild(list);
          sheetGroups.appendChild(box);
        });
      }
    }

    // Only one panel open at a time
    function openPanel(name) {
      closePanel();
      var box = ctx.root.querySelector('.cf-group[data-group="' + name + '"]');
      if (!box) return;
      box.querySelector('.cf-pill').setAttribute('aria-expanded', 'true');
      box.querySelector('.cf-panel').hidden = false;
      openGroup = name;
    }

    // focusPill: give the focus back to the pill (closing with Esc)
    function closePanel(focusPill) {
      if (!openGroup || !ctx.root) return;
      var box = ctx.root.querySelector('.cf-group[data-group="' + openGroup + '"]');
      openGroup = null;
      if (!box) return;
      var pill = box.querySelector('.cf-pill');
      pill.setAttribute('aria-expanded', 'false');
      box.querySelector('.cf-panel').hidden = true;
      if (focusPill) pill.focus();
    }

    // Selected options, pill texts ("Kilates: 14k") and the "Limpiar filtros" link
    function syncSelected() {
      ctx.root.querySelectorAll('.cf-option').forEach(function (button) {
        var group = groupNamed(button.dataset.filter);
        var option = optionOf(group, button.dataset.value);
        var selected = !!option && isSelected(group, option);
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      ctx.groups.forEach(function (group) {
        if (group.kind === 'custom' && adapter.syncGroup) adapter.syncGroup(ctx, group);
        var pill = ctx.root.querySelector('#' + adapter.prefix + 'Pill-' + group.name);
        if (!pill) return;
        var text = pill.querySelector('.cf-pill__text');
        var value;
        if (group.kind === 'custom') {
          value = adapter.pillValue(ctx, group);
        } else {
          var option = optionOf(group, ctx.state[group.name]);
          value = option && (option.short || option.label);
        }
        text.textContent = group.label;
        if (value) {
          text.appendChild(document.createTextNode(': '));
          text.appendChild(create('strong', '', value));
        }
        pill.classList.toggle('is-active', !!value);
      });

      // The desktop link hides; the sheet's "Limpiar" stays in place, disabled
      var active = Object.keys(ctx.state).length;
      ctx.root.querySelectorAll(CLEAR).forEach(function (clear) {
        if (clear.classList.contains('cf-sheet__clear')) clear.disabled = !active;
        else clear.hidden = !active;
      });

      // Number of filters applied, on the mobile "Filtros" button
      var badge = ctx.root.querySelector(BADGE);
      if (badge) {
        badge.textContent = active;
        badge.hidden = !active;
      }
    }

    // "Ver X productos" in the sheet, with the products in the results
    function syncResults() {
      var applyButton = ctx.root.querySelector(SHEET_APPLY);
      if (!applyButton || typeof ctx.data.count !== 'number') return;
      applyButton.textContent = 'Ver ' + ctx.data.count + (ctx.data.count === 1 ? ' producto' : ' productos');
    }

    /* Mobile sheet --------------------------------------------------------- */

    function openSheet() {
      var sheet = ctx.root.querySelector(SHEET);
      if (!sheet || sheetOpen) return;
      closePanel();
      clearTimeout(sheetTimer);
      sheet.hidden = false;
      void sheet.offsetHeight; // start the slide-up from the closed position
      sheet.classList.add('is-open');
      document.documentElement.classList.add(SCROLL_LOCK);
      ctx.root.querySelector(SHEET_OPEN).setAttribute('aria-expanded', 'true');
      sheetOpen = true;
      sheet.querySelector('.cf-sheet__close').focus({ preventScroll: true });
    }

    // focusToggle: give the focus back to the "Filtros" button
    function closeSheet(focusToggle) {
      var sheet = ctx.root && ctx.root.querySelector(SHEET);
      if (!sheet || !sheetOpen) return;
      sheetOpen = false;
      sheet.classList.remove('is-open');
      document.documentElement.classList.remove(SCROLL_LOCK);
      var toggle = ctx.root.querySelector(SHEET_OPEN);
      toggle.setAttribute('aria-expanded', 'false');
      // Hidden once the slide-down is over (.cf-sheet__panel transition)
      sheetTimer = setTimeout(function () {
        if (!sheetOpen) sheet.hidden = true;
      }, 300);
      if (focusToggle) toggle.focus({ preventScroll: true });
    }

    // Keep Tab inside the sheet while it's open (aria-modal)
    function trapFocus(e) {
      var panel = SHEET + ' .cf-sheet__panel ';
      var items = ctx.root.querySelectorAll(panel + 'button:not([disabled]), ' + panel + 'a[href], ' + panel + 'input:not([disabled])');
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    // Dim and disable the options that would give zero results: the adapter
    // says which (isEmpty), from ctx.data and what loadAvailability loaded.
    // Unknown (request failed or still loading): left enabled. The selected
    // option is never disabled, so it can always be unchecked.
    function syncAvailable() {
      ctx.root.querySelectorAll('.cf-option').forEach(function (button) {
        var group = groupNamed(button.dataset.filter);
        var option = optionOf(group, button.dataset.value);
        var selected = !!option && isSelected(group, option);
        var empty = !selected && !!adapter.isEmpty(ctx, button.dataset.filter, button.dataset.value);
        button.disabled = empty;
        button.classList.toggle(EMPTY, empty);
      });

      // Links to other collections keep the current sort order
      var sort = new URLSearchParams(location.search).get('sort_by');
      ctx.root.querySelectorAll('.cf-link[data-href]').forEach(function (link) {
        link.href = link.dataset.href + (sort ? '?sort_by=' + encodeURIComponent(sort) : '');
      });
    }

    /* Loading and swapping the results ------------------------------------- */

    function setLoading(on) {
      ctx.root.querySelector(GRID).classList.toggle(LOADING, on);
      var applyButton = ctx.root.querySelector(SHEET_APPLY);
      if (applyButton) applyButton.classList.toggle(LOADING, on);
    }

    // push: true adds a history entry, 'replace' replaces the current one
    function load(url, push, scrollUp) {
      url = withoutSectionId(url);
      if (request) request.abort();
      var current = request = new AbortController();
      setLoading(true);

      var sectionUrl = new URL(url, location.href);
      sectionUrl.searchParams.set('section_id', adapter.sectionId);

      var section = fetch(sectionUrl.toString(), { signal: current.signal, credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        });

      // In parallel with the section. Pagination and sorting keep the
      // filters, so what was already loaded for them is reused.
      var key = adapter.availabilityKey(ctx, ctx.state);
      var availability = availFor === key ? Promise.resolve(ctx.avail) : adapter.loadAvailability(ctx, ctx.state, current.signal);

      // Blocking (collection): the results wait for it, so the options are
      // never wrong. Not blocking (search): the results show first and the
      // options are dimmed when it arrives.
      if (!adapter.blocking) {
        availability.then(function (result) {
          if (!ctx.root || adapter.availabilityKey(ctx, ctx.state) !== key) return;
          ctx.avail = result;
          availFor = key;
          syncAvailable();
        });
      }

      Promise.all([section, adapter.blocking ? availability : null])
        .then(function (results) {
          var doc = new DOMParser().parseFromString(results[0], 'text/html');
          if (!doc.querySelector(GRID)) throw new Error('No product grid in the response');
          var entry = {};
          entry[adapter.historyKey] = true;
          if (push === 'replace') history.replaceState(entry, '', url);
          else if (push) history.pushState(entry, '', url);
          if (adapter.blocking) {
            ctx.avail = results[1];
            availFor = key;
          }
          render(doc);
          if (scrollUp) scrollToGrid();
        })
        .catch(function (error) {
          if (error.name === 'AbortError') return;
          // Anything unexpected: fall back to a normal page load
          window.location.href = url;
        })
        .then(function () {
          if (request !== current) return;
          request = null;
          setLoading(false);
        });
    }

    // What the options of the current view need, without the results. A
    // click before it arrives aborts this and load() asks for it again; with
    // a load() in flight, nothing (it asks for it too).
    function loadAvailable() {
      if (request) return;
      var current = request = new AbortController();
      var key = adapter.availabilityKey(ctx, ctx.state);
      adapter.loadAvailability(ctx, ctx.state, current.signal).then(function (result) {
        if (request !== current) return;
        request = null;
        ctx.avail = result;
        availFor = key;
        syncAvailable();
      });
    }

    function render(doc) {
      var grid = ctx.root.querySelector(GRID);
      var $ = window.jQuery;

      // Tooltips of the old cards would stay open if removed while showing
      if ($ && $.fn.tooltip) $(grid).find('[data-toggle="tooltip"]').tooltip('dispose');
      grid.innerHTML = doc.querySelector(GRID).innerHTML;

      // Product count (not rendered when there are no products)
      var count = ctx.root.querySelector(COUNT);
      var newCount = doc.querySelector(COUNT);
      if (count && newCount) count.replaceWith(newCount);
      else if (count) count.remove();
      else if (newCount) ctx.root.querySelector('.collection-hero__divider').after(newCount);

      var pages = ctx.root.querySelectorAll(PAGINATION);
      var newPages = doc.querySelectorAll(PAGINATION);
      pages.forEach(function (el, i) {
        el.innerHTML = newPages[i] ? newPages[i].innerHTML : '';
      });

      ctx.data = readJSON(doc, adapter.dataId) || ctx.data;
      syncSelected();
      syncAvailable();
      syncResults();

      var sort = ctx.root.querySelector(SORT);
      if (sort && ctx.data.sort) sort.value = ctx.data.sort;

      syncPageMeta(doc);
      activate(grid);
      if (adapter.onRender) adapter.onRender(ctx, doc);
    }

    // Tab title and breadcrumb ("Inicio / Aretes / 14k + dama") live in the
    // layout, outside the section: copy them from the section's <template>,
    // rendered by Liquid like after a reload (snippets/seo-title,
    // breadcrumb-builtin)
    function syncPageMeta(doc) {
      var meta = doc.querySelector('#' + adapter.metaId);
      if (!meta) return;

      var title = meta.content.querySelector('[data-cf-title]');
      if (title) document.title = title.textContent.replace(/\s+/g, ' ').trim();

      var crumbs = meta.content.querySelector('.breadcrumb-list');
      var current = document.querySelector('.breadcrumbs-section .breadcrumb-list');
      if (crumbs && current) current.innerHTML = crumbs.innerHTML;
    }

    // Hearts of the card grid without the tooltip: on touch screens the
    // "Lista de Deseados" bubble stayed stuck after a tap. The title becomes
    // the button's aria-label (it has no visible text). Run before the theme
    // (theme.js, on document ready) or activate() turn tooltips on.
    // A copy lives in assets/home-page.js (product tabs of the home page):
    // change both together.
    function plainHearts(scope) {
      var $ = window.jQuery;
      scope.querySelectorAll('.cc-grid .button-group .wishlist[data-toggle="tooltip"]').forEach(function (heart) {
        if ($ && $.fn.tooltip) $(heart).tooltip('dispose');
        var label = heart.getAttribute('title') || heart.getAttribute('data-original-title');
        if (label) heart.setAttribute('aria-label', label);
        heart.removeAttribute('title');
        heart.removeAttribute('data-original-title');
        heart.removeAttribute('data-toggle');
      });
    }

    // Set up again what the theme only sets up on page load
    function activate(grid) {
      var $ = window.jQuery;

      plainHearts(grid.parentNode);

      if ($) {
        // Lazy-loaded images (theme.js, CollectionTemplateActivation)
        if ($.fn.lazyload) $(grid).find('img.lazyload').lazyload();
        // Wishlist / compare / quick view tooltips (theme.js)
        if ($.fn.tooltip) $(grid).find('[data-toggle="tooltip"]').tooltip();
      }

      // Hearts of products already in the wishlist (wishlist.js; clicks are delegated)
      if (window.Wishlist && window.Wishlist.activate) window.Wishlist.activate(grid);

      // Disabled arrows and the current page lose their link (pagination.liquid)
      ctx.root.querySelectorAll(PAGINATION + ' .disabled a, ' + PAGINATION + ' li.active a').forEach(function (a) {
        a.removeAttribute('href');
      });
      ctx.root.querySelectorAll(PAGINATION + ' a[href]').forEach(function (a) {
        var href = a.getAttribute('href');
        if (href !== '#') a.setAttribute('href', withoutSectionId(href));
      });

      // Color swatches swap the card image (theme.js)
      grid.querySelectorAll('.product-color li label').forEach(function (label) {
        label.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var link = label.parentNode.querySelector('.hidden a');
          var card = label.closest('.product-wrapper');
          if (!link || !card) return;
          card.querySelectorAll('.popup_cart_image').forEach(function (img) {
            img.setAttribute('src', link.getAttribute('href'));
          });
        });
      });
    }

    // To the top of the grid, below the fixed header (.sticky.is-sticky).
    // Only upwards (pagination), unless always (the sheet's "Ver X productos").
    // The fixed header's height is --header-sticky-h (assets/header.css): not
    // fixed yet, the desktop header block is taller than the bar it becomes.
    // Measured as before if the variable is missing. Without .sticky ("Header
    // Sticky Enable" off) there is no fixed header.
    function scrollToGrid(always) {
      var header = 0;
      var stickies = document.querySelectorAll('.sticky');
      if (stickies.length) {
        header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-sticky-h')) || 0;
      }
      if (!header) {
        stickies.forEach(function (el) {
          header = Math.max(header, el.getBoundingClientRect().height);
        });
      }
      var top = ctx.root.querySelector(GRID).getBoundingClientRect().top + window.pageYOffset - header - 20;
      if (always || top < window.pageYOffset) window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    }

    /* Events --------------------------------------------------------------- */

    // Pagination without reloads, and "Limpiar filtros": every filter goes,
    // the sort order stays (the sheet stays open). Delegated: a clear button
    // can come with the results (search, no products with these filters).
    function onRootClick(e) {
      if (e.target.closest(CLEAR)) {
        ctx.state = {};
        closePanel();
        syncSelected();
        load(adapter.url(ctx, ctx.state), true, false);
        return;
      }

      var link = e.target.closest(PAGINATION + ' a[href]');
      if (!link) return;
      var href = link.getAttribute('href');
      if (!href || href === '#') return;
      // Ctrl/Cmd/Shift-click and middle click still open a new tab or window
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      load(link.href, true, true);
    }

    window.addEventListener('popstate', function (e) {
      if (!ctx.root || !e.state || !e.state[adapter.historyKey]) return;
      ctx.state = adapter.readState(ctx);
      closePanel();
      syncSelected();
      load(location.href, false, false);
    });

    // Outside click and Esc close the open panel
    document.addEventListener('click', function (e) {
      if (openGroup && !e.target.closest('.cf-group')) closePanel();
    });

    document.addEventListener('keydown', function (e) {
      if (sheetOpen) {
        if (e.key === 'Escape') closeSheet(true);
        else if (e.key === 'Tab') trapFocus(e);
        return;
      }
      if (openGroup && e.key === 'Escape') closePanel(true);
    });

    // Growing past mobile with the sheet open: close it (it isn't shown there)
    function onBreakpoint() {
      if (!mobile.matches) closeSheet();
    }
    if (mobile.addEventListener) mobile.addEventListener('change', onBreakpoint);
    else if (mobile.addListener) mobile.addListener(onBreakpoint);

    // Public: sort without reloading (used by the sort select)
    function sortBy(value) {
      if (!ctx.root) return false;
      load(adapter.url(ctx, ctx.state, value), true, false);
      return true;
    }

    function init() {
      ctx.root = document.querySelector(adapter.root);
      ctx.data = ctx.root && readJSON(ctx.root, adapter.dataId);
      ctx.groups = ctx.root && ctx.data && adapter.groups(ctx);
      if (!ctx.root || !ctx.data || !ctx.groups) {
        ctx.root = null;
        return;
      }

      ctx.state = adapter.readState(ctx);
      ctx.avail = {};
      availFor = '';
      openGroup = null;
      sheetOpen = false;
      document.documentElement.classList.remove(SCROLL_LOCK);
      buildFilters();
      syncSelected();
      syncAvailable();
      syncResults();
      plainHearts(ctx.root);
      ctx.root.addEventListener('click', onRootClick);

      var sort = ctx.root.querySelector(SORT);
      if (sort) {
        sort.addEventListener('change', function () {
          sortBy(sort.value);
        });
      }

      // Mobile sheet: "Filtros" opens it; X and the backdrop close it;
      // "Ver X productos" closes it and goes to the grid
      var toggle = ctx.root.querySelector(SHEET_OPEN);
      if (toggle) toggle.addEventListener('click', openSheet);
      ctx.root.querySelectorAll(SHEET_CLOSE).forEach(function (el) {
        el.addEventListener('click', function () {
          closeSheet(true);
        });
      });
      var applyButton = ctx.root.querySelector(SHEET_APPLY);
      if (applyButton) {
        applyButton.addEventListener('click', function () {
          closeSheet(true);
          scrollToGrid(true);
        });
      }

      // What the options of the loaded view need (collection: only with
      // filters checked, from a shared link or a reload)
      if (adapter.loadOnInit(ctx, ctx.state)) loadAvailable();

      // Mark the entry the page was loaded with, so "back" can return to it
      var entry = {};
      entry[adapter.historyKey] = true;
      history.replaceState(Object.assign({}, history.state, entry), '');

      if (adapter.onInit) adapter.onInit(ctx);
    }

    // The section is re-rendered when edited in the theme editor
    document.addEventListener('shopify:section:load', function (e) {
      if (e.target.querySelector(adapter.root)) init();
    });

    // The tags have defer, but don't depend on it: the markup and the filter
    // data must exist before init()
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }

    return { sortBy: sortBy };
  }

  FilterBar.readJSON = readJSON;
  FilterBar.create = create;
  window.FilterBar = FilterBar;
})();
